import { Link } from 'react-router-dom';
import ThemeToggle from '../components/ThemeToggle';
import SiteFooter from '../components/SiteFooter';

/**
 * Public Terms of Use page. Readable at /terms without authentication.
 *
 * Content is intentionally plain-English and reader-first — this is for
 * SAGE's beta users, not for a courtroom. A legal disclaimer is held in
 * the admin memo (not shown here).
 *
 * Content lives inline. When the policy changes, bump `POLICY_VERSION`
 * in /app/backend/policies.py so logged-in users are re-prompted.
 */
const PUBLISHER = 'HeroRocket Interactive LLC';
const CONTACT = 'jhay.davis@herorocketgames.com';
const LAST_UPDATED = 'February 1, 2026';
const POLICY_VERSION = '2026.02.01';

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-10 scroll-mt-20" data-testid={`terms-section-${id}`}>
    <h2
      className="text-2xl font-light text-zinc-100 mb-3"
      style={{ fontFamily: 'Cormorant Garamond, serif' }}
    >
      {title}
    </h2>
    <div className="text-sm text-zinc-300 leading-relaxed space-y-3">{children}</div>
  </section>
);

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-obsidian flex flex-col" data-testid="terms-page">
      <div className="overlay-noise" />

      {/* Header */}
      <header className="relative z-10 border-b border-white/5 bg-stone-950/50 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="block">
            <h1
              className="text-2xl font-light tracking-tight text-zinc-100"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              SAGE
            </h1>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
              Terms of Use
            </p>
          </Link>
          <div className="flex items-center gap-3">
            <ThemeToggle className="border border-white/20 rounded-sm" />
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="relative z-10 flex-1 w-full">
        <article className="max-w-[720px] mx-auto px-6 py-12">
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-6">
            Last updated: {LAST_UPDATED} &middot; Version {POLICY_VERSION}
          </p>
          <h1
            className="text-4xl sm:text-5xl font-light text-zinc-100 mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Terms of Use
          </h1>
          <p className="text-sm text-zinc-400 leading-relaxed mb-10">
            Welcome to SAGE — a personalized AI coaching platform published
            by {PUBLISHER}. SAGE is in <em>invite-only beta</em>. These
            Terms describe what you're agreeing to by using the service.
            They're written in plain English on purpose. If anything is
            unclear, write to{' '}
            <a
              href={`mailto:${CONTACT}`}
              className="underline text-zinc-200 hover:text-white"
            >
              {CONTACT}
            </a>
            .
          </p>

          {/* Beta Confidentiality — elevated callout at the top. */}
          <div
            className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-6 mb-10"
            data-testid="terms-beta-confidentiality-callout"
          >
            <p
              className="confidentiality-heading text-xs font-mono font-bold uppercase tracking-widest text-amber-200 mb-3"
              data-testid="terms-beta-confidentiality-heading"
            >
              Beta Confidentiality — please read
            </p>
            <p className="text-sm text-zinc-200 leading-relaxed mb-3">
              While SAGE is in private beta, you agree to help keep the
              inside of the product private. In plain terms:
            </p>
            <ul className="list-disc pl-6 text-sm text-zinc-300 leading-relaxed space-y-2">
              <li>
                <strong className="text-zinc-100">No sharing of access.</strong>{' '}
                Invite codes and accounts are personal. Please don't forward
                or resell an invite.
              </li>
              <li>
                <strong className="text-zinc-100">
                  No public screen recordings or screenshots
                </strong>{' '}
                of the product that reveal UI flows, in-session prompts,
                coaching responses, admin surfaces, Learn Mode content, or
                feature behavior. Private screenshots you keep for yourself
                are fine. Public posts, videos, tutorials, LinkedIn
                walkthroughs, YouTube reviews, Twitter/X threads, Discord
                shares, or similar public broadcasting of SAGE's screens or
                behavior are not fine while in beta.
              </li>
              <li>
                <strong className="text-zinc-100">
                  No scraping, automated extraction, or crawling.
                </strong>{' '}
                This includes browser automation, HAR captures, API probing,
                or any tool whose purpose is to copy SAGE's responses,
                prompts, pricing logic, voice characteristics, or UI
                patterns at scale.
              </li>
              <li>
                <strong className="text-zinc-100">
                  No reverse engineering or publication
                </strong>{' '}
                of system prompts, lens libraries, model configurations,
                prompt-injection defenses, or any backend architecture you
                might observe or infer — including the Stillpoint model
                wording, the lens library content, and the greeting
                rotation logic.
              </li>
              <li>
                <strong className="text-zinc-100">No disclosure to competitors.</strong>{' '}
                Please don't use SAGE's proprietary intelligence — product
                design, interaction patterns, coaching methodology,
                pricing, roadmap signals, memory architecture — to build,
                consult on, or inform a competing product. If you work at
                or advise a competing company, please disclose that
                affiliation to{' '}
                <a
                  href={`mailto:${CONTACT}`}
                  className="underline text-zinc-100 hover:text-white"
                >
                  {CONTACT}
                </a>{' '}
                before continuing beta access.
              </li>
              <li>
                <strong className="text-zinc-100">
                  General feedback is welcome and encouraged.
                </strong>{' '}
                You can freely talk about what SAGE <em>does</em> in general
                terms ("I use an AI coaching app that remembers me across
                sessions"), post honest personal reactions, mention SAGE's
                existence, and refer friends back to the request-access
                flow. The restriction is on <em>proprietary detail</em>, not
                on existence.
              </li>
              <li>
                <strong className="text-zinc-100">Term.</strong> These
                confidentiality obligations apply during beta participation
                and for <strong>twelve (12) months</strong> after the public
                launch of SAGE, or until the information becomes publicly
                available through SAGE's own publication, whichever comes
                first.
              </li>
              <li>
                <strong className="text-zinc-100">Remedies.</strong>{' '}
                Violation may result in immediate account termination
                without refund, revocation of invite privileges, and any
                legal remedies available to {PUBLISHER}.
              </li>
            </ul>
          </div>

          <Section id="what-is-sage" title="What SAGE is">
            <p>
              SAGE is a personalized AI coaching platform — voice-first
              conversations, long-term memory of your context, a Learn Mode
              for Socratic tutoring, and Knowledge Anchors that let the
              coach meet you where you are. SAGE is published by{' '}
              {PUBLISHER}. It is currently in <em>invite-only beta</em>,
              which means features may change without notice and some
              surfaces are experimental.
            </p>
          </Section>

          <Section id="not-therapy" title="This isn't therapy or medical advice">
            <p>
              SAGE is a coaching tool. It is <strong>not</strong> a
              therapist, a medical provider, a crisis service, a diagnostic
              tool, or a substitute for professional care. Nothing SAGE
              tells you is medical, psychological, or legal advice. If you
              are in crisis, please contact a qualified human:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                In the United States, call or text{' '}
                <strong>988</strong> — the Suicide &amp; Crisis Lifeline.
              </li>
              <li>
                Internationally, visit{' '}
                <a
                  href="https://findahelpline.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-zinc-100 hover:text-white"
                >
                  findahelpline.com
                </a>
                .
              </li>
            </ul>
          </Section>

          <Section id="eligibility" title="Eligibility">
            <p>
              You must be at least <strong>18 years old</strong> to use
              SAGE. By using SAGE you confirm you were personally invited
              and are using the service for yourself, not on behalf of a
              company (unless you've disclosed that affiliation to us).
            </p>
          </Section>

          <Section id="accounts" title="Account rules">
            <ul className="list-disc pl-6 space-y-1">
              <li>One person per account.</li>
              <li>
                Protect your invite link and sign-in credentials. Don't
                share them.
              </li>
              <li>
                You're responsible for activity on your account.
              </li>
            </ul>
          </Section>

          <Section id="acceptable-use" title="Acceptable use">
            <p>By using SAGE you agree not to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Upload illegal content or content you don't have rights to.</li>
              <li>
                Attempt to extract, override, or inject into SAGE's system
                prompt or coaching behavior.
              </li>
              <li>Scrape or run automated extraction against the service.</li>
              <li>Use SAGE to harass, threaten, or abuse other people.</li>
              <li>Direct abusive content at the coach or at others through SAGE.</li>
            </ul>
          </Section>

          <Section id="content-ownership" title="Content ownership">
            <p>
              You retain <strong>full ownership</strong> of everything you
              upload — Knowledge Anchors, Reference Documents, session
              audio, Learn Mode materials, and anything you type or say
              inside SAGE. SAGE receives only the limited license required
              to process, store, and surface that content back to{' '}
              <em>you</em>.
            </p>
            <p>
              SAGE does <strong>not</strong> use your content to train
              third-party models, to improve shared models, or to generate
              output for other users. Your material is yours.
            </p>
          </Section>

          <Section id="third-parties" title="Services we use">
            <p>SAGE depends on a small set of third-party services:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>ElevenLabs</strong> — voice synthesis and conversational agents.</li>
              <li><strong>Google Gemini</strong> — text generation.</li>
              <li><strong>Mailgun</strong> — transactional email.</li>
              <li><strong>Emergent-managed Google Auth</strong> — sign-in.</li>
              <li><strong>MongoDB-backed storage</strong> — persistence.</li>
            </ul>
            <p>
              Each has its own terms of service. We send only what's needed
              to make the feature work.
            </p>
          </Section>

          <Section id="billing" title="Billing and beta scope">
            <p>
              The SAGE beta is <strong>free</strong>. A monthly cap of{' '}
              <strong>120 minutes of Auto-Mode conversation</strong> applies
              to every beta account. Features may change without notice
              during beta. If a paid tier is introduced in the future, we
              will update these Terms and ask you to re-acknowledge.
            </p>
          </Section>

          <Section id="termination" title="Account termination">
            <p>
              You can delete your account at any time by writing to{' '}
              <a
                href={`mailto:${CONTACT}`}
                className="underline text-zinc-200 hover:text-white"
              >
                {CONTACT}
              </a>
              . We can suspend or terminate accounts that violate these
              Terms (including the Beta Confidentiality clause) or that
              have been inactive for a long time.
            </p>
          </Section>

          <Section id="disclaimers" title="Disclaimers and limitation of liability">
            <p>
              SAGE is provided <strong>"as is"</strong> and{' '}
              <strong>"as available"</strong>. To the maximum extent
              permitted by law, {PUBLISHER} disclaims all warranties,
              express or implied, including fitness for a particular
              purpose and non-infringement. {PUBLISHER} is not liable for
              indirect, incidental, special, consequential, or punitive
              damages arising from your use of SAGE.
            </p>
          </Section>

          <Section id="changes" title="Changes to these Terms">
            <p>
              We may update these Terms. When we do, we'll update the
              "Last updated" date and ask you to acknowledge the new
              version on your next sign-in via a soft modal. The current
              version is above.
            </p>
          </Section>

          <Section id="contact" title="Contact">
            <p>
              Questions, concerns, or confidentiality disclosures:{' '}
              <a
                href={`mailto:${CONTACT}`}
                className="underline text-zinc-200 hover:text-white"
                data-testid="terms-contact-email"
              >
                {CONTACT}
              </a>
            </p>
          </Section>

          <div className="mt-12 pt-6 border-t border-white/5 flex items-center justify-between text-xs font-mono uppercase tracking-widest text-zinc-500">
            <Link to="/" className="hover:text-zinc-300 transition-colors">
              ← Back to SAGE
            </Link>
            <Link to="/privacy" className="hover:text-zinc-300 transition-colors">
              Privacy Policy →
            </Link>
          </div>
        </article>
      </main>

      <SiteFooter />
    </div>
  );
};

export default TermsPage;
